/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input, ViewChild } from '@angular/core';
import {
    type ActionCloseArgs, type Command, type HttpResponse, type PromptConfigFull, CAPACITY_UNIT, FormGroup,
    HttpService, SlideWindowAbstract, TimeService, UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { NfsPermissionE } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { LicenseKey } from 'app/common/constants/license-key';
import { VstoreIdE } from 'app/common/constants/vstore';
import { RemoteExecuteService, ToolService } from 'app/common/services';
import {
    type AVServer, type CIFSClient, type CIFSShare, type FileSystem, type FileSystemResource, type FileSystemResourceCIFS,
    type FileSystemResourceNFS, type HyperCdpSchedule, type NFSClient, type NFSShare, type Vstore, type WorkloadType,
} from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';
import { WorkModeE } from 'app/feature/protection/config/hyper-metro-domain/hyper-metro-domain.enum';
import { DEFAULT_CDP_SCHEDULE_NAME } from 'app/feature/protection/schedule/hyper-cdp/hyper-cdp.const';
import { ScheduleService } from 'app/feature/protection/schedule/hyper-cdp/schedule/schedule.service';
import { SYSTEM_VSTORE_ID } from 'app/feature/service/block-service/lun-group/luns.const';
import { CreateFsScanMissionPolicyCardComponent } from 'app/feature/settings/file-storage-service/antivirus/antivirus-policy/filesystem-scan-mission/create-fs-scan-mission-policy-card/create-fs-scan-mission-policy-card.component';
import { ScanPolicyTypeE } from 'app/feature/settings/file-storage-service/antivirus/antivirus-str.service';
import { StoragePoolService } from 'app/feature/system/storage-pool/storage-pool.service';
import { StoragePoolStrService } from 'app/feature/system/storage-pool/storage-pool-str.service';
import { type Subscriber, Observable, of } from 'rxjs';
import { catchError, finalize, flatMap, map, tap } from 'rxjs/operators';

import { OffLineFileModeE } from '../../share/cifs-share/cifs-share-str.service';
import { CIFSUserPermissionsE, DomainTypeE, UserTypeE } from '../../share/cifs-share/client-cifs-share/client-clifs-share';
import { NfsShareService } from '../../share/nfs-share/nfs-share.service';
import { type SmartguiCifsshareReceived, type SmartguiNfsshareReceived } from '../../share/share';
import { CreateFileSystemComponent, FileSystemEntryE } from '../create-file-system/create-file-system.component';
import { FileSystemService } from '../file-system.service';
import { FileSystemStrService, SecurityModeE, VaaiSwitchE } from '../file-system-str.service';
import { type HandleSmartguiFsData, type SmartguiFilesystemReceived } from '../fs-model';
import { CifsShareService } from './../../share/cifs-share/cifs-share.service';

@Component({
    selector: 'app-fs-smartgui-info',
    templateUrl: './fs-smartgui-info.component.html',
    styleUrls: ['./fs-smartgui-info.component.scss'],
    providers: [
        StoragePoolService,
        StoragePoolStrService,
    ],
})
export class FsSmartGuiInfoComponent extends SlideWindowAbstract implements OnInit {
    @ViewChild('ondemandCard') ondemandCard: CreateFsScanMissionPolicyCardComponent;
    @ViewChild('onaccessCard') onaccessCard: CreateFsScanMissionPolicyCardComponent;
    @Input() vstore: SelectOption;
    // 由哪个地方入口打开的创建文件系统
    @Input() entry: FileSystemEntryE;

    UNPLACEHOLDER = UNPLACEHOLDER;
    DEFAULT_CDP_SCHEDULE_NAME = DEFAULT_CDP_SCHEDULE_NAME;
    FileSystemEntryE = FileSystemEntryE;
    i18n = this.str.i18n;
    loading = false;
    pageReady = false;
    windowVisible = false;
    SecurityModeE = SecurityModeE;
    VaaiSwitchE = VaaiSwitchE;
    OffLineFileModeE = OffLineFileModeE;

    // 页面关闭回传数据
    closeArgs = {
        refresh: false,
        data: null,
    };

    // 时间错，加载文件的时候就初始化好，避免重复计算
    timestamp = this.timeService.formatUTC(this.timeService.currTime * 1e3, 'YYMMDDHHmmss', false).split(' ')[0];

    // 获取的智能推荐数据
    fsSmartguiData: SmartguiFilesystemReceived = {};
    // 用于页面展示数据
    handleSmartguiFsData: HandleSmartguiFsData = {};
    // 直接下发的数据
    fsSmartguiPostData: FileSystem = {};
    // 已选中的应用类型
    selectedWorkloadType: WorkloadType;
    defaultCdpSchedule;

    hasCdpPermission = this.permissionService.checkShow(['@hyper_cdp_W'], [LicenseKey.HyperCDP]);
    hasSharePermission = this.permissionService.hasPermission('share_W');
    hasCdpReadPermission = this.permissionService.hasPermission('@hyper_cdp_R');

    // 共享的开关，根据是否有Share权限来判断
    shareSwitch = {
        nfs: this.hasSharePermission,
        cifs: this.hasSharePermission,
    };

    // 加入内置CDP计划，根据是否有CDP权限来判断
    addToCdpScheduleSwitch = this.hasCdpPermission;

    // 权限的资料描述
    permissionStr = {
        nfs: this.i18n.permissionsWith(1, this.i18n.allClient),
        cifs: this.i18n.permissionsWith(1, this.i18n.everyone),
    };

    // cifs
    cifs: CIFSShare = {};
    // nfs
    nfs: NFSShare = {};

    loadingCifs = false;

    workloadCreateTypeMap = this.str.workloadCreateTypeMap;

    // 共享路径
    sharePath = {
        nfs: UNPLACEHOLDER,
        cifs: UNPLACEHOLDER,
    };

    // nfs权限的默认数据
    nfsPermission: NFSClient = {
        NAME: '*',
        _type: 'host',
        ACCESSVAL: NfsPermissionE.readAndWrite, // 读写
        ACCESSKRB5: NfsPermissionE.none, // 无权限
        ACCESSKRB5I: NfsPermissionE.none, // 无权限
        ACCESSKRB5P: NfsPermissionE.none, // 无权限
        SYNC: '0',
        ALLSQUASH: '1',
        ROOTSQUASH: '0', // 默认 root_squash
        SECURE: '1',
    };

    // cifs权限的默认数据
    cifsPermission: CIFSClient = {
        ID: 'Everyone-0',
        NAME: '@Everyone',
        DOMAINTYPE: DomainTypeE.Local,
        PERMISSION: CIFSUserPermissionsE.All,
        userType: UserTypeE.Everyone,
    };

    supportGuaranteedCapacity = false;
    supportDedupeAndCompression = false;

    vstorePairId: string;
    fsHyperMetroDomain: FsHyperMetroDomain;
    remoteStoragePoolOpts = [];
    remoteVstoreName: string;
    remoteVstoreId: string;
    getHyperMetroPairError = false;
    remoteParentId = null;
    isHYVstore = false;

    ProductConfig = ProductConfig;
    SWITCH = SWITCH;

    // 是否展示防病毒
    showAntivirus = false;

    ScanPolicyTypeE = ScanPolicyTypeE;
    vm: FormGroup;
    SYSTEM_VSTORE_ID = SYSTEM_VSTORE_ID;
    constructor(
        injector: Injector,
        public str: FileSystemStrService,
        private service: FileSystemService,
        private storagePoolService: StoragePoolService,
        public utilService: UtilService,
        private toolService: ToolService,
        private timeService: TimeService,
        private CIFSService: CifsShareService,
        private NfsService: NfsShareService,
        private scheduleService: ScheduleService,
        private http: HttpService,
        private remoteExecuteService: RemoteExecuteService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.checkShow().subscribe(() => {
            if (this.entry !== FileSystemEntryE.Main) {
                this.shareSwitch.nfs = false;
                this.shareSwitch.cifs = false;
            }
            this.pageReady = true;
            this.windowVisible = true;

            this.supportGuaranteedCapacity = this.permissionService.isLicenseExist(
                [[LicenseKey.GuaranteedCapacity, LicenseKey.SpecialLicense]],
            );
            this.supportDedupeAndCompression = this.toolService.supportDedupeAndCompression();

            // 获取文件系统智能推荐数据
            this.getFsSmartguiData();
            if (!ProductConfig.isHideCifs) {
                // 获取CIFS共享名的推荐数据
                this.getCifsSmartguiData();
            }
            // 获取NFS共享名的推荐数据
            this.getNfsSmartguiData();
            // 获取默认CDP定时计划
            this.getDefaultCdpSchedule();

            this.getDefaultAntivirusData();
        }, () => this.close());
    }

    checkShow(): Observable<string> {
        let vstore$ = of(null);
        if (this.vstore && this.vstore.value) {
            vstore$ = this.service.getVstore(this.vstore.value, { mask: true }).pipe(
                map(({ data }) => {
                    this.isHYVstore = this.isHyperMetroVstore(data);
                    return data;
                }),
                // 查询租户信息失败当做普通租户处理
                catchError(() => of(null)),
            );
        }

        // 不能使用forkOrder，需要等到checkLicense后才能执行checkPermission
        let vstore: Vstore;
        return vstore$.pipe(
            map((value) => vstore = value),
            flatMap(() => this.checkPermission(vstore)),
        );
    }

    checkPermission(vstore?: Vstore): Observable<string> {
        let permissions;
        if (!vstore) {
            // 所有租户
            permissions = ['storage_pool_R'];
        } else if (!this.isHyperMetroVstore(vstore)) {
            // 选择特性租户， 租户是非双活
            permissions = ['storage_pool_R'];
        } else {
            permissions = ['task_W', 'hyper_metro_pair_W', 'hyper_metro_domain_R', 'storage_pool_R'];
        }

        return this.permissionService.checkDepPermission(permissions);
    }

    /**
     * 弹出license不存在弹窗
     */
    showNoLicensePropmt(licenseStr: string): Observable<void> {
        return Observable.create((observer: Subscriber<void>) => {
            const cancel = (): void => {
                this.promptService.close();
                observer.error();
                observer.complete();
            };

            this.promptService.prompt({
                title: this.i18nService.get('common.LicenseNotExist_title'),
                content: this.i18nService.get('common.licenseNotExistMsg', [licenseStr]),
                onOk: cancel,
                onCancel: cancel,
            });
        });
    }

    // 获取文件系统的智能推荐数据
    getFsSmartguiData(): void {
        // 所有租户下获取系统租户的推荐数据
        this.service.getSmartguiFilesystem(this.vstore.value || VstoreIdE.sys,
            {
                mask: true,
                requestTimeoutTip: this.i18n.smartRecommendDataTimeoutMsg,
            })
            .subscribe(({ data }) => {
                // 判定是否底层系统报错返回空对象，若为空则不处理数据
                if (!Object.keys(data).length) {
                    return;
                }

                this.fsSmartguiData = { ...this.fsSmartguiData, ...data };
                this.handleSmartguiFsData = {
                    ...this.handleSmartguiFsData,
                    ...this.handleData(),
                };

                // 获取共享路径
                this.sharePath.nfs = '/' + this.handleSmartguiFsData.name + '/';
                this.sharePath.cifs = '/' + this.handleSmartguiFsData.name + '/';
                // 获取租户
                this.getSmartguiVstore();
                // 获取存储池
                this.getStoragePool(this.fsSmartguiData.parentid);
                // 获取应用类型
                this.getWorkloadType(this.fsSmartguiData.workloadtypeid);
            });
    }

    getHyperMetroPair(vstoreId: string): void {
        this.service.getVstorePair(vstoreId).pipe(
            flatMap(({ data }) => {
                if (data && data.length) {
                    this.vstorePairId = data[0].ID;
                    this.remoteVstoreName = data[0].REMOTEVSTORENAME;
                    this.remoteVstoreId = data[0].REMOTEVSTOREID;
                    return this.service.getFsHyperMetroDomain(data[0].DOMAINID);
                }
                this.vstorePairId = null;
                throw new Error('no pair');
            }),
            flatMap(({ data }) => {
                this.fsHyperMetroDomain = data;
                return this.remoteExecuteService.get(JSON.parse(data.REMOTEDEVICES)[0].devId,
                    this.http.makeUrl('storagePool.getBatch').setParams({ vstoreId: this.remoteVstoreId }),
                    {},
                );
            }),
            map(({ data }) => {
                if (data && data.length) {
                    return data.map((item) => ({
                        value: item.ID,
                        label: item.NAME,
                    }));
                }
                return [];
            }),
        ).subscribe((data: SelectOption[]) => {
            this.remoteStoragePoolOpts = this.vstorePairId ? data : [];
        }, () => {
            if (this.vstorePairId === undefined) {
                this.getHyperMetroPairError = true;
            }
            this.remoteStoragePoolOpts = [];
        });
    }

    /**
     * 获取CIFS名称的智能推荐数据
     */
    getCifsSmartguiData(): void {
        // 如果没有传入vstore 就选默认租户0
        const vstoreId = this.vstore.value || '0';
        this.CIFSService.getSmartguiCIFS(vstoreId, { mask: true, requestTimeoutTip: this.i18n.smartRecommendDataTimeoutMsg })
            .subscribe(({ data }: { data: SmartguiCifsshareReceived }) => {
                this.loadingCifs = false;
                this.cifs.NAME = data.NAME[0];
                if (data.ENABLENOTIFY) {
                    this.cifs.ENABLENOTIFY = data.ENABLENOTIFY;
                }

                if (data.ENABLECA) {
                    this.cifs.ENABLECA = data.ENABLECA;
                }

                if (data.smb3encryptionenable) {
                    this.cifs.smb3EncryptionEnable = data.smb3encryptionenable;
                }

                if (data.unencryptedaccess) {
                    this.cifs.unencryptedAccess = data.unencryptedaccess;
                }

                if (data.ABEENABLE) {
                    this.cifs.ABEENABLE = data.ABEENABLE;
                }
                if (data.ENABLESHOWSNAPSHOT) {
                    this.cifs.ENABLESHOWSNAPSHOT = data.ENABLESHOWSNAPSHOT;
                }
                if (data.ENABLESHOWPREVIOUSVERSIONS) {
                    this.cifs.ENABLESHOWPREVIOUSVERSIONS = data.ENABLESHOWPREVIOUSVERSIONS;
                }
                if (data.OFFLINEFILEMODE) {
                    this.cifs.OFFLINEFILEMODE = data.OFFLINEFILEMODE;
                }
            });
    }

    /**
     * 获取NFS名称的智能推荐数据
     */
    getNfsSmartguiData(): void {
        // 如果没有传入vstore 就选默认租户0
        const vstoreId = this.vstore.value || SYSTEM_VSTORE_ID;
        this.NfsService.getSmartguiNFS(vstoreId, { mask: true, requestTimeoutTip: this.i18n.smartRecommendDataTimeoutMsg })
            .subscribe(({ data }: { data: SmartguiNfsshareReceived }) => {
                if (data.ENABLESHOWSNAPSHOT) {
                    this.nfs.ENABLESHOWSNAPSHOT = data.ENABLESHOWSNAPSHOT === 'true';
                }
            });
    }

    // 获取共享名称，用文件系统名+切割
    getShareName(fsName: string): string {
        if (!fsName) {
            return '';
        }
        const tempStr = '_CIFS_Share_';
        const timeLength = this.timestamp.length;
        // 80 是指CIFS共享名称的长度。
        return this.utilService.byteSubstring(fsName, 80 - timeLength - tempStr.length) + tempStr + this.timestamp;
    }

    getDefaultCdpSchedule(): void {
        if (!this.hasCdpReadPermission) {
            return;
        }
        const rest = this.scheduleService.getBatchRest(ScheduleTypeE.File);
        this.http.get(rest).subscribe(({ data }) => {
            this.defaultCdpSchedule = data.find((item: HyperCdpSchedule) => item.isBuildinSchedule === 'true');
            if (this.defaultCdpSchedule) {
                this.defaultCdpSchedule.strategy = this.scheduleService.renderStrategy(this.defaultCdpSchedule);
            }
        }, () => {
            this.defaultCdpSchedule = {};
        });
    }

    /**
     * CDP计划状态渲染
     */
    statusRender = (status: string): string => {
        const item = this.str.scheduleStatusOpts.find((a) => a.value === status);
        return item ? item.label : UNPLACEHOLDER;
    };

    /**
     * 处理数据，用于页面展示
     */
    handleData(): HandleSmartguiFsData {
        return {
            name: this.fsSmartguiData.NAME[0],
            securitymode: this.fsSmartguiData.securitystyle,
            naslockingpolicy: this.fsSmartguiData.naslockingpolicy,
            capacity: this.capacityConvert(Number(this.fsSmartguiData.capacity)),
            capacitythreshold: this.fsSmartguiData.capacitythreshold,
            isshowsnapdir: this.fsSmartguiData.isshowsnapdir === 'true',
            atime: this.fsSmartguiData.atime,
            atimeupdatemode: Number(this.fsSmartguiData.atimeupdatemode),
            snapshotreserveper: Number(this.fsSmartguiData.snapshotreserveper),
            vaaiswitch: this.fsSmartguiData.vaaiswitch,

        };
    }

    /**
     * 智能推荐租户处理
     */
    getSmartguiVstore(): void {
        let vstoreId;
        if (!this.vstore.value) {
            // 当选择所有租户进行标记
            this.fsSmartguiData.selectAllVstore = true;
            const defaultVstoreID = '0';
            vstoreId = defaultVstoreID;
            // 当选择所有租户时使用默认租户
            this.service.getVstore(defaultVstoreID, { mask: true })
                .subscribe(({ data }) => {
                    this.handleSmartguiFsData.vstoreName = data.NAME;
                    this.fsSmartguiData.vstoreItem = {
                        label: data.NAME,
                        value: data.ID,
                    };
                });
        } else {
            this.fsSmartguiData.selectAllVstore = false;
            this.handleSmartguiFsData.vstoreName = this.vstore.label;
            this.fsSmartguiData.vstoreItem = {
                label: this.vstore.label,
                value: this.vstore.value,
            };
            vstoreId = this.vstore.value;
        }

        if (this.permissionService.checkShow(['hyper_metro_pair_R'])) {
            this.getHyperMetroPair(vstoreId);
        }
    }

    /**
     * 智能推荐存储池处理
     */
    getStoragePool(id: string): void {
        if (!this.fsSmartguiData.parentid) {
            return;
        }
        this.storagePoolService.get(id)
            .subscribe(({ data }) => {
                this.fsSmartguiData.storagePoolItem = {
                    NAME: data.NAME,
                    ID: data.ID,
                };
                this.handleSmartguiFsData.storagepool = data.NAME;
            });
    }

    /**
     * 容量转换
     */
    capacityConvert(capacity: number): string {
        const capObj = this.service.getValueAndUnit(capacity);
        return capObj.label;
    }

    /**
     * 获取应用类型
     */
    getWorkloadType(id: string): void {
        this.service.getWorkloadType(id)
            .subscribe(({ data }) => {
                this.handleSmartguiFsData.workloadtype = data.NAME;
                this.selectedWorkloadType = data;
            });
    }

    /**
     * 构建下发命令
     */
    buildCmds(): Command[] {
        const cmds: Command[] = [this.buildCreateFileSystemCmd()];
        return cmds;
    }

    collectionNFSShareParams(vstoreId?: string, vmNFS?: NFSShare, vmNFSClient?: NFSClient[]): FileSystemResource {
        const result: FileSystemResource = {
        };
        const nfs: FileSystemResourceNFS = {
            ID: vmNFS.ID,
            DESCRIPTION: vmNFS.DESCRIPTION,
            ENABLESHOWSNAPSHOT: ProductConfig.isHideNfsSnapShot ? undefined : String(this.nfs.ENABLESHOWSNAPSHOT),
            vstoreId,
        };
        if (vmNFSClient) {
            delete nfs.ID;
            const clients: NFSClient[] = [];
            vmNFSClient.forEach((data) => {
                clients.push({
                    ACCESSVAL: data.ACCESSVAL,
                    ACCESSKRB5: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5,
                    ACCESSKRB5I: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5I,
                    ACCESSKRB5P: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5P,
                    ALLSQUASH: data.ALLSQUASH,
                    NAME: data.NAME,
                    ROOTSQUASH: data.ROOTSQUASH,
                    SECURE: data.SECURE,
                    SYNC: data.SYNC,
                    vstoreId,
                });
            });
            nfs.auth = clients;
        }
        result.nfs = [nfs];
        return result;
    }

    collectionCIFSShareParams(vstoreId?: string, vmCIFS?: CIFSShare, vmCIFSUser?: CIFSClient[]): FileSystemResource {
        const result: FileSystemResource = {
        };

        const cifs: FileSystemResourceCIFS = {
            ID: vmCIFS.ID,
            NAME: vmCIFS.NAME,
            DESCRIPTION: vmCIFS.DESCRIPTION,
            ENABLENOTIFY: vmCIFS.ENABLENOTIFY,
            ENABLECA: vmCIFS.ENABLECA,
            ABEENABLE: vmCIFS.ABEENABLE,
            ENABLESHOWSNAPSHOT: vmCIFS.ENABLESHOWSNAPSHOT,
            ENABLESHOWPREVIOUSVERSIONS: vmCIFS.ENABLESHOWSNAPSHOT,
            smb3EncryptionEnable: vmCIFS.smb3EncryptionEnable,
            unencryptedAccess: vmCIFS.unencryptedAccess,
            vstoreId,
            OFFLINEFILEMODE: vmCIFS.OFFLINEFILEMODE || OffLineFileModeE.None,
        };
        if (vmCIFSUser) {
            delete cifs.ID;
            const users: CIFSClient[] = [];
            vmCIFSUser.forEach((data) => {
                users.push({
                    NAME: data.NAME,
                    DOMAINTYPE: data.DOMAINTYPE,
                    PERMISSION: data.PERMISSION,
                    vstoreId,
                });
            });
            cifs.auth = users;
        }
        result.cifs = [cifs];
        return result;
    }

    /**
     * 收集下发防病毒的公共参数
     *
     */
    collectAntivirusCommonParam(): any {
        const tempVstoreId = this.vstore.value || SYSTEM_VSTORE_ID;
        return {
            switch: true,
            fsName: this.vm.value.name,
            vstoreId: tempVstoreId,
            vstoreName: this.handleSmartguiFsData.vstoreName,
        };
    }

    /**
     * 是否开启防病毒服务
     *
     * @returns boolean
     */
    hasUsedAntivirusService(): boolean {
        return (this.onaccessCard && this.onaccessCard.switch) || (this.ondemandCard && this.ondemandCard.switch);
    }

    getCifsShareParams(): CIFSShare {
        // 构造cifs数据
        const cifsShare: CIFSShare = {
            NAME: this.cifs.NAME,
            SHAREPATH: this.sharePath.cifs,
            DESCRIPTION: '',
            ENABLENOTIFY: this.cifs.ENABLENOTIFY,
            ENABLECA: this.cifs.ENABLECA,
            ABEENABLE: this.cifs.ABEENABLE,
            ENABLESHOWSNAPSHOT: this.cifs.ENABLESHOWSNAPSHOT,
            ENABLESHOWPREVIOUSVERSIONS: this.cifs.ENABLESHOWPREVIOUSVERSIONS,
            smb3EncryptionEnable: this.cifs.smb3EncryptionEnable,
            unencryptedAccess: this.cifs.unencryptedAccess,
            OFFLINEFILEMODE: this.cifs.OFFLINEFILEMODE || OffLineFileModeE.None,
        };

        return cifsShare;
    }

    /**
     * 构建创建文件系统的命令
     */
    buildCreateFileSystemCmd(): Command {
        this.fsSmartguiPostData = { ...this.handlePostData(this.fsSmartguiData) };
        let remoteStorage = null;
        // 加入内置定时计划
        this.fsSmartguiPostData.scheduleName = this.addToCdpScheduleSwitch ? DEFAULT_CDP_SCHEDULE_NAME : 'NONE';
        // 如果开启了共享或者防病毒，要构造omtask的接口
        const isHyperMetro = this.vstorePairId;
        const shareValid = this.shareSwitch.nfs || (this.shareSwitch.cifs && !ProductConfig.isHideCifs);
        if (shareValid || isHyperMetro || this.hasUsedAntivirusService()) {
            let nfsParam: FileSystemResource;
            let cifsParam: FileSystemResource;
            if (this.shareSwitch.nfs) {
                // 构造nfs数据
                const nfsShare: NFSShare = {
                    SHAREPATH: this.sharePath.nfs,
                    DESCRIPTION: '',
                };
                nfsParam = this.collectionNFSShareParams(this.fsSmartguiPostData.vstoreId, nfsShare, [this.nfsPermission]);
            }
            if (this.shareSwitch.cifs && !ProductConfig.isHideCifs) {
                // 构造cifs数据
                const cifsShare: CIFSShare = this.getCifsShareParams();
                cifsParam = this.collectionCIFSShareParams(this.fsSmartguiPostData.vstoreId, cifsShare, [this.cifsPermission]);
            }

            if (isHyperMetro) {
                remoteStorage = { remoteParentId: this.remoteParentId };
            }
            return {
                name: this.str.createResult(this.fsSmartguiPostData.NAME),
                action: this.service.createByOmtask(this.fsSmartguiPostData.NAME, {
                    fileSystem: this.fsSmartguiPostData,
                    ...remoteStorage,
                    ...nfsParam,
                    ...cifsParam,
                    ...this.showAntivirus ?
                        this.service.collectAntivirusData(this.ondemandCard, this.onaccessCard, this.vm, this.collectAntivirusCommonParam()) :
                        {},
                }),
            };
        } else {
            return {
                name: this.str.createResult(this.fsSmartguiPostData.NAME),
                action: this.service.create(this.fsSmartguiPostData, { restNameParams: [this.fsSmartguiPostData.NAME], doException: false })
                    .pipe(
                        tap(({ data }) => this.closeArgs = { ...this.closeArgs, data }),
                    ),
            };
        }
    }

    /**
     * 处理数据,用于下发
     */
    handlePostData(data: SmartguiFilesystemReceived): FileSystem {
        const filesystem: FileSystem = {
            NAME: data.NAME[0],
            PARENTID: data.parentid,
            CAPACITYTHRESHOLD: data.capacitythreshold,
            securityStyle: data.securitystyle,
            ISSHOWSNAPDIR: data.isshowsnapdir,
            ATIME: data.atime,
            ATIMEUPDATEMODE: data.atime === 'true' ? data.atimeupdatemode : '4294967295',
            workloadTypeId: Number(data.workloadtypeid),
            CAPACITY: data.capacity,
            vstoreId: data.vstoreItem.value,
            ...(data.naslockingpolicy ? {
                nasLockingPolicy: data.naslockingpolicy,
            } : {}),
            SNAPSHOTRESERVEPER: data.snapshotreserveper,
            vaaiSwitch: data.vaaiswitch,
        };

        // 不支持文件系统快照相关字段
        if (ProductConfig.isHideFsSnapshotFeature) {
            // 快照空间比例
            delete filesystem.SNAPSHOTRESERVEPER;
            // 快照目录是否可见
            delete filesystem.ISSHOWSNAPDIR;
        }

        // 文件系统是否支持VAAI能力开关
        if (!ProductConfig.isSupportVAAISwitch) {
            delete filesystem.vaaiSwitch;
        }

        return filesystem;
    }

    /**
     * 跳转到修改页面，即创建文件系统页面
     */
    smartGuiModify(): void {
        this.doAction(CreateFileSystemComponent, {
            vstore: this.vstore,
            entry: this.entry,
            fsSmartguiData: this.fsSmartguiData,
            shareSwitch: this.shareSwitch,
            cdpSwicth: this.addToCdpScheduleSwitch,
            smartGuiCifs: this.cifs,
            smartGuiNfs: this.nfs,
            remoteParentId: this.remoteParentId,
            onClose: (args: ActionCloseArgs) => {
                if (args.refresh) {
                    this.closeArgs = { refresh: true, data: args.data };
                    this.hide();
                }
            },
        });
    }

    /**
     * 复写exec，如果走omtask，就不走cmd的流程。
     */
    exec(): void {
        const shareValid = this.shareSwitch.nfs || (this.shareSwitch.cifs && !ProductConfig.isHideCifs);
        const tempResult = shareValid || this.vstorePairId;
        if (tempResult || this.hasUsedAntivirusService()) {
            const cmds = this.buildCmds();
            if (cmds && cmds.length === 1) {
                this.loading = true;
                (cmds[0].action as Observable<HttpResponse>).pipe(finalize(() => {
                    this.loading = false;
                })).subscribe(() => {
                    this.closeArgs.refresh = true;
                    this.hide();
                });
            } else {
                this.hide();
            }
        } else {
            super.exec();
        }
    }

    /**
     * 重写确定按钮功能
     */
    onOk(): void {
        // 双活场景必须选择存储池
        if ((this.vstorePairId || this.getHyperMetroPairError) && !this.remoteParentId) {
            return;
        }

        if (!Object.keys(this.fsSmartguiData).length || this.loadingCifs) {
            return;
        }
        if (this.fsSmartguiData?.parentid) {
            this.validate().subscribe((valid) => {
                if (!valid) {
                    return;
                }
                // 双活租户，且双活文件系统时，需要检查NAS双活license
                const isMetroOrAp = this.fsHyperMetroDomain &&
                    (this.fsHyperMetroDomain.workMode === WorkModeE.HyperMetro || this.fsHyperMetroDomain.workMode === WorkModeE.AP);
                if (this.isHYVstore && isMetroOrAp) {
                    if (!this.permissionService.isLicenseExist([LicenseKey.FileHyperMetro])) {
                        this.showNoLicensePropmt(LicenseKey.FileHyperMetro).subscribe();
                    } else {
                        this.permissionService.checkLicense([LicenseKey.FileHyperMetro]).subscribe(
                            () => {
                                this.doSave();
                            },
                        );
                    }
                } else {
                    this.doSave();
                }
            });
        } else {
            // 若未创建存储池,则不下发并跳转到创建页面进行错误提示
            this.fsSmartguiData.storagePoolItem = {
                NAME: '',
                ID: '',
            };
            this.doAction(CreateFileSystemComponent, {
                vstore: this.vstore,
                entry: this.entry,
                fsSmartguiData: this.fsSmartguiData,
                shareSwitch: this.shareSwitch,
                smartGuiCifs: this.cifs,
                smartGuiNfs: this.nfs,
                onClose: (args: ActionCloseArgs) => {
                    if (args.refresh) {
                        this.closeArgs = { refresh: true, data: args.data };
                        this.hide();
                    }
                },
            });
        }
    }

    doSave(): void {
        const promptConfs = this.prompt();
        if (promptConfs?.length) {
            this.promptService.multi(promptConfs, () => {
                this.promptService.close();
                this.exec();
            });
        } else {
            this.exec();
        }
    }

    isHyperMetroVstore(vstore: Vstore): boolean {
        return Boolean(Number(vstore.metroCount));
    }

    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];
        const CAPACITY_3GB = this.utilService.adaptUnit(3, CAPACITY_UNIT.GB, CAPACITY_UNIT.SECTOR).value;
        const fsCapacity = Number(this.fsSmartguiData.capacity);
        const vaaiSwitchState = this.fsSmartguiData.vaaiswitch;

        // 创建文件系统容量不大于3GB时，需要高危提示
        if (fsCapacity <= CAPACITY_3GB) {
            prompts.push({
                id: 'prompt-fs-smartgui-info-createMsg',
                opType: 'warning',
                title: this.i18n.createTitle,
                content: this.i18n.createMsg,
                width: 700,
            });
        }

        if (this.shareSwitch.cifs) {
            if (this.cifs.smb3EncryptionEnable === 'true') {
                prompts.push({
                    id: 'prompt-fs-smartgui-info-SMB3EncryptEnabledTip',
                    opType: 'warning',
                    title: this.i18n.createCIFS,
                    content: this.i18n.SMB3EncryptEnabledTip,
                    width: 700,
                });
            }
            if (this.cifs.unencryptedAccess === 'true') {
                prompts.push({
                    id: 'prompt-fs-smartgui-info-enableUnencryptedAccessTip',
                    opType: 'warning',
                    title: this.i18n.createCIFS,
                    content: this.i18n.enableUnencryptedAccessTip,
                    width: 700,
                });
            }
        }
        // 创建文件系统开启GUI，需要高危提示
        if (this.fsSmartguiData.vaaiswitch &&
            vaaiSwitchState === VaaiSwitchE.ON) {
            prompts.push({
                id: 'prompt-fs-smartgui-info-vaaiMsg',
                opType: 'warning',
                title: this.i18n.createTitle,
                content: this.i18n.vaaiMsg,
                width: 700,
            });
        }
        return prompts;
    }

    /**
     * 获取默认防病毒数据
     *
     */
    getDefaultAntivirusData(): void {
        if (!this.service.showAntvirusNecessaryCondition()) {
            this.showAntivirus = false;
            return;
        }
        // smartgui推荐，如果没有选租户，使用默认租户
        const tempVstoreId = this.vstore.value || SYSTEM_VSTORE_ID;

        // 查是否开启放病毒服务器
        this.service.getAntivirusServer(tempVstoreId)
            .subscribe(({ data }: { data: AVServer[] }) => {
                if (data.length) {
                    this.initVm();
                    this.showAntivirus = true;
                }
            });
    }

    initVm(): void {
        this.vm = new FormGroup({});
    }
}
